import {Socket} from "socket.io-client";
import {SocketManager} from "./SocketManager.ts";
import useStore from "../store.ts";
import {postUpdate} from "../worker/DataWorkerManager.ts";
import {mergeRow} from "../utils/TypeUtils.ts";
import {WrapperSubscription} from "../channel/WrapperSubscription.ts";
import {isReactive, reactive, toRaw} from "vue";
import { LayoutManager, LayoutResult} from "../layout/LayoutManager.ts";
import {RecoveryHandler} from "./RecoveryHandler.ts";
import {ConnectionState} from "./ConnectionState.ts";
import {integrationManager} from "../integrations/IntegrationManager.ts";
import {captureScreen} from "../utils/CaptureUtils.ts";
import {
    getChannelSubscriptionFields,
    getRuleFieldIds,
    widenChannelFieldsForRule,
} from "../utils/ChannelFieldCollector.ts";
import {UpdateOrigin, type UpdateContextHandle} from "../utils/UpdateContextRegistry.ts";
import type {Channel} from "../channel/Channel.ts";
import {orchestrator} from "../utils/Orchestrator.ts";
import {datasheetRowCapacity} from "../utils/PureElementUtils.ts";
import {dropRefreshedFromSlowPathBuffer, slowPathMark} from "../utils/DatasheetUtils.ts";
import {postRenderRequest, postUpdateGeometry, triggerDatasheetBuild} from "../worker/RenderWorkerManager.ts";
import * as Sentry from "@sentry/vue";
import {CacheManager} from "../cache/CacheManager.ts";
import type {ScreenshotPolicy} from "../types.ts";

/* see docs/EXTRACTED_COMMENTS.md#c763 */
const parseScreenshotPolicy = (raw: any): ScreenshotPolicy | null => {
    if (raw == null || typeof raw !== "object") return null;
    const interval = Number(raw.interval);
    const retention = Number(raw.retention);
    const stamp = raw.offload_timestamp;
    if (!isFinite(interval) || !isFinite(retention) || stamp == null || typeof stamp !== "object") {
        console.warn(`[SocketManager] Ignoring malformed screenshot policy: ${JSON.stringify(raw)}`);
        return null;
    }
    const hour = Number(stamp.hour);
    const minute = Number(stamp.minute);
    const second = Number(stamp.second);
    if (!isFinite(hour) || !isFinite(minute) || !isFinite(second)) {
        console.warn(`[SocketManager] Ignoring malformed screenshot policy: ${JSON.stringify(raw)}`);
        return null;
    }
    return {interval, retention, offload_timestamp: {hour, minute, second}};
};

/* ---------- Minimal payload typings (extend as needed) ---------- */
interface RequestLayoutPayload {
    result?: LayoutResult;
    error?: string;
}

interface RequestAssignPage {
    result?: AssignPage
}

interface RequestScreendump {
    result?: Screendump
}

interface Screendump {
    device_id: string
}

interface AssignPage {
    page: number
}
interface BuildLayoutPayload {
    result?: LayoutResult;
}

interface PageActionPayload {
    result?: string | number;
}
interface PlaylistPayload {
    data: unknown;
    name: string;
}

interface RequestPlaylistPayload {
    // Not-found replies carry the playlist name here as a bare string.
    result?: {
        playlist_name: string;
        [k: string]: any;
    } | string | null;
    message?: string | null;
    error?: string | null;
}

interface RulePayload {
    data: { rule: string; hash?: string; rule_hash?: string };
    name: string;
}

interface RequestRulePayload {
    // Not-found replies carry the rule name here as a bare string (#c782).
    result?: { rule: string; name: string; hash?: string; rule_hash?: string } | string | null;
    message?: string | null;
    error?: string | null;
}

interface ChannelRowPayload {
    result?: {
        data: any;       // row data shape is app-specific
        channel: number;
        row: number;
    };
    session_id?: string; // envelope-level layout-generation token (SocketResponse)
}

interface ChannelDeletePayload {
    result?: {
        channel: number;
        row: number;
    };
    session_id?: string;
}

interface ChannelOutdatedPayload {
    result?: {
        channel: number;
    }
}

interface ChannelSubscribePayload {
    result?: {
        channel_id: string;
        rows: number;
    };
    session_id?: string;
}

interface GenericMessagePayload {
    message: string;
    error?: string;
    result?: any;
}

export class SocketListener {
    private readonly socket: Socket;
    private readonly socketManager: SocketManager;
    private readonly layoutManager: LayoutManager | null;
    private readonly recovery: RecoveryHandler;

    private initialFired = false;
    // see docs/EXTRACTED_COMMENTS.md#c123
    private subscriptionsActivated = false;
    // see the comment on retryUnmatchedDynamicSubs()
    private unmatchedRetryDone = false;
    // see docs/EXTRACTED_COMMENTS.md#c125
    private buttonStatusRetries = new Map<string, number>();
    private static readonly BUTTON_STATUS_MAX_RETRIES = 8;
    // see docs/EXTRACTED_COMMENTS.md#c721
    private channelStallTimer: number | undefined;
    private static readonly CHANNEL_STALL_IDLE_MS = 10_000;
    // see docs/EXTRACTED_COMMENTS.md#c804
    private static readonly RESET_DROPPED_ORIGINS: ReadonlySet<UpdateOrigin> = new Set([
        UpdateOrigin.ChannelUpdate,
        UpdateOrigin.ChannelAdd,
        UpdateOrigin.ChannelDelete,
        UpdateOrigin.ChannelClear,
    ]);

    constructor(socket: Socket, socketManager: SocketManager, recovery: RecoveryHandler) {
        this.socket = socket;
        this.socketManager = socketManager;
        this.layoutManager = socketManager.layoutManager;
        this.recovery = recovery;
    }

    loadEvents(): void {
        const store = useStore();

        /* ------------------------ Connection events ------------------------ */

        this.socket.on("connect", async () => {
            console.log("[Socket] Connected to backend.");
            await this.recovery.handleConnect();
        });

        this.socket.on("connnection", async () => {
            console.log("test");
        });

        this.socket.on("disconnect", (reason?: string) => {
            console.log("[Socket] Disconnected from backend.", reason ?? "");
            this.recovery.handleDisconnect(reason);
        });

        this.socket.on("connect_error", (error) => {
            console.warn("[Socket] connect_error", error);
            this.recovery.handleConnectError(error);
        });

        this.socket.on("reconnect_attempt", (attempt) => {
            console.log("[Socket] reconnect_attempt", attempt);
            store.connection.state = ConnectionState.Connecting;
        });

        this.socket.on("reconnect_error", (error) => {
            console.warn("[Socket] reconnect_error", error);
            this.recovery.handleConnectError(error);
        });

        // see docs/EXTRACTED_COMMENTS.md#c126
        this.socket.on("reconnect_failed", () => {
            console.warn("[Socket] reconnect_failed");
            this.recovery.handleDisconnect("reconnect_failed");
        });
        this.socket.on("connect_failed", () => {
            console.warn("[Socket] connect_failed");
            this.recovery.handleDisconnect("connect_failed");
        });

        /* ------------------------ Layout requests ------------------------- */

        this.socket.on("request_layout_hash", async ({result, error}: RequestLayoutPayload) => {
            if (this.layoutManager == null) return;
            await this.layoutManager.handleLayoutResponse(result, error);
        });

        this.socket.on("request_layout", async ({result, error}: RequestLayoutPayload) => {
            if (this.layoutManager == null) return;
            // see docs/EXTRACTED_COMMENTS.md#c127
            await this.layoutManager.handleLayoutResponse(result, error);
        });


        this.socket.on("device_action.assign_page", async ({result}: RequestAssignPage) => {
            if (this.layoutManager == null) return;
            await this.layoutManager.handleAssignPage(result?.page);
        });

        this.socket.on("device_action.cluster_update_row_start", async ({result}) => {
            if (this.layoutManager == null) return;
            if (!result) return;
            const {channel, row} = result;
            // see docs/EXTRACTED_COMMENTS.md#c128
            if (store.cluster.row_start == row && store.cluster.channel == channel) return;
            console.log(`[SocketManager] Received request to update row start to ${row}`);
            store.cluster.row_start = row;
            store.cluster.channel = channel;
            await this.layoutManager.updateRowStart();
        });

        this.socket.on("device_action.request_cluster_set_notification", async ({}) => {
            if (this.layoutManager == null) return;
            console.log(`[SocketManager] Received notification for cluster`);
            store.cluster.enabled = true;
        });

        this.socket.on("device_action.request_cluster_row_usage", async ({}) => {
            if (this.layoutManager == null) return;
            await this.layoutManager.handleRowUsage();
        });

        this.socket.on("device_action.request_cluster_update_sheet_channel", async ({}) => {
            if (this.layoutManager == null) return;
            await this.layoutManager.handleClusterChannel();
        });


        this.socket.on("device_action.reboot_device", async () => {
            if (this.layoutManager == null) return;
            console.log(`[SocketManager] Rebooting device`);
            await integrationManager.rebootDevice();
        });

        this.socket.on("device_action.send_monitor_command", async ({result}: any) => {
           console.log("[SocketManager] Received send monitor command", result);
           const { cmd, data } = result;
           await integrationManager.monitor(cmd, data);
        });

        this.socket.on("device_action.disconnect_device", async () => {
            if (this.layoutManager == null) return;
            console.log(`[SocketManager] Disconnecting device`);
            this.socket.disconnect();
        });

        // RECONFIGURE_PLAYLIST arrives as `device_action.request_playlist` (backend spec §3.12):
        // `result` is the list of playlist names, or null for all.
        this.socket.on("device_action.request_playlist", async ({result}: any) => {
            console.log(`[SocketManager] Reconfiguring playlists: ${JSON.stringify(result)}`);
            store.playlistManager.reloadPlaylists(Array.isArray(result) ? result : null);
        });


        this.socket.on("device_action.reconfigure_device", async () => {
            console.log(`[SocketManager] Reconfiguring device`);
            window.location.reload();
        });


        this.socket.on("device_action.reconfigure_layout", async () => {
            console.log(`[SocketManager] Reconfiguring layout`);
            await store.socketManager.requestLayout();
        });

        this.socket.on("device_action.restart_display", async () => {
            console.log(`[SocketManager] Restarting display`);
            window.location.reload();
        });

        this.socket.on("device_action.request_screendump", async ({result}: RequestScreendump) => {
            if (!result) return;
            const {device_id} = result;
            console.log(`[SocketManager] Creating screendump`);
            await captureScreen(device_id);
        });

        this.socket.on("device_action.request_audit_upload", async () => {
            console.log(`[SocketManager] Uploading stored screenshots`);
            await integrationManager.requestAuditUpload(this.socketManager.getDeviceId());
        });

        this.socket.on("error", async ({error}: any) => {
            this.recovery.handleTransportError(error);
        });

        this.socket.on("authenticate", async ({error, result}: any) => {
            if (error && error != "An authentication request has already been made.") {
                console.error(error);
                this.recovery.handleTransportError(error);
                if (typeof window !== "undefined") {
                    window.dispatchEvent(new CustomEvent("auth:required"));
                }
                return;
            }
            if (store.error.includes("Maximum number")) return;
            store.error = "";
            if (!result) return;

            console.log("[SocketManager] Authentication was successful.")
            const screenshotPolicy = parseScreenshotPolicy(result.screenshot_policy);
            store.device_settings = {
                status_bar: false,
                content_url: result.url,
                default_page: parseInt(result.default_page),
                error_page: parseInt(result.error_page),
                allowed_pages: result.pages,
                disconnect_policy: result.disconnect_policy,
                screenshot_policy: screenshotPolicy
            };

            // Push the plain parsed object, not the reactive read-back: the native
            // transports structured-clone / JSON their payload.
            if (screenshotPolicy != null) {
                void integrationManager.sendScreenshotPolicy(screenshotPolicy, this.socketManager.getDeviceId());
            }
            // see docs/EXTRACTED_COMMENTS.md#c461
            if (store.layout == null) {
                const defaultPage = store.device_settings.default_page;
                const firstAllowed = Number(store.device_settings.allowed_pages?.[0]);
                if (!isNaN(defaultPage)) {
                    store.layout = defaultPage;
                } else if (!isNaN(firstAllowed)) {
                    store.layout = firstAllowed;
                } else {
                    store.layout = 1;
                }
            }

            // see docs/EXTRACTED_COMMENTS.md#c462
            const policyLayoutId = store.device_settings.disconnect_policy?.assign_action?.layout_id;
            if (policyLayoutId != null && String(policyLayoutId) !== String(store.layout ?? "")) {
                this.layoutManager!.requestLayout(false, policyLayoutId);
            }

            // see docs/EXTRACTED_COMMENTS.md#c463
            this.socketManager.setAuthenticated(true);
            if (store.error == "" && store.connection.state == ConnectionState.Authenticating)
                this.recovery.bumpConnectionState(ConnectionState.Ready);
        });

        // Ack for the standalone device_info event. `forwarded` says whether the
        // backend pushed the details straight to DCS or only stored them for the
        // next connect — informational either way, nothing to recover from.
        this.socket.on("device_info", async ({error, result}: any) => {
            if (error) {
                console.warn(`[SocketManager] Device info was rejected: ${error}`);
                return;
            }
            console.debug(`[SocketManager] Device info stored (forwarded=${result?.forwarded === true}).`);
        });

        this.socket.on("request_build_layout", async ({result}: BuildLayoutPayload) => {
            if (this.layoutManager == null) return;
            await this.layoutManager.handleBuildLayout(result);
        });

        this.socket.on("request_page_action", ({result}: PageActionPayload) => {
            if (result == null) return;
            try {
                parseInt(result as any);
                if (isNaN(Number(result))) {
                    console.error("[SocketManager] Reset page action failed");
                    // HARD CODE:
                    store.loaded = false;
                    store.layout = 1001;
                    return;
                }
            }catch(error) {
                console.log("[SocketManager] Reconfiguring page action failed: " + JSON.stringify(error));
                return;
            }
            store.loaded = false;
            store.layout = Number(result);
        });

        /* ------------------------ Button status --------------------------- */

        this.socket.on("request_button_status", (payload: GenericMessagePayload) => {
            const { result } = payload;
            if (!result) return;
            if (payload.error && payload.error.includes("Try again in a moment")) {
                const attempts = (this.buttonStatusRetries.get(result.object_id) ?? 0) + 1;
                if (attempts > SocketListener.BUTTON_STATUS_MAX_RETRIES) {
                    console.warn(`[SocketListener] request_button_status for ${result.object_id} abandoned after ${attempts - 1} retries`);
                    this.buttonStatusRetries.delete(result.object_id);
                    return;
                }
                this.buttonStatusRetries.set(result.object_id, attempts);
                setTimeout(() => {
                    const comp = store.components[result.object_id];
                    if (comp) this.socketManager.requestButtonStatus(comp)
                }, Math.min(1000 * 2 ** (attempts - 1), 30_000));
                return;
            }

            const {object_id, activated} = result;
            this.buttonStatusRetries.delete(object_id);
            const comp = store.components[object_id];
            if (!comp?.button_props) return;
            comp.button_props.disabled = !activated;
        });

        /* ------------------------ Playlist events ------------------------- */

        this.socket.on("playlist", ({data, name}: PlaylistPayload) => {
            const playlist = store.playlistManager.getPlaylist(name);
            if (!playlist) return;
            store.playlistManager.markPresent(name);
            playlist.setValue(data);
            playlist.getCallbacks().forEach((cb: Function) => cb(playlist));
        });

        this.socket.on("request_playlist", (payload: RequestPlaylistPayload) => {
            if (SocketListener.isPlaylistNotFound(payload)) {
                this.handlePlaylistNotFound(payload);
                return;
            }
            const result = payload.result;
            if (!result || typeof result === "string") return;
            const playlist = store.playlistManager.getPlaylist(result.playlist_name);
            if (!playlist) return;
            store.playlistManager.markPresent(result.playlist_name);
            playlist.setValue(result);
            playlist.getCallbacks().forEach((cb: Function) => cb(playlist));
        });

        /* ------------------------ Rule events ----------------------------- */

        this.socket.on("rule", ({data, name}: RulePayload) => {
            const prevFieldIds = new Set<number>(getRuleFieldIds(name));
            const incomingHash = data?.hash ?? data?.rule_hash;
            store.ruleManager.handleRuleUpdate(name, data?.rule, incomingHash);
            const nextFieldIds = getRuleFieldIds(name);
            const added = nextFieldIds.filter((fieldId) => !prevFieldIds.has(fieldId));
            widenChannelFieldsForRule(name, added);
        });

        this.socket.on("request_rule", (payload: RequestRulePayload) => {
            if (SocketListener.isRuleNotFound(payload)) {
                this.handleRuleNotFound(payload);
                return;
            }
            const result = payload.result;
            if (!result || typeof result === "string") return;
            const {rule: value, name, hash, rule_hash} = result;
            const prevFieldIds = new Set<number>(getRuleFieldIds(name));
            const incomingHash = hash ?? rule_hash;
            store.ruleManager.handleRuleUpdate(name, value, incomingHash);
            const nextFieldIds = getRuleFieldIds(name);
            const added = nextFieldIds.filter((fieldId) => !prevFieldIds.has(fieldId));
            widenChannelFieldsForRule(name, added);
        });

        /* ------------------------ Channel: add/update/delete -------------- */

        this.socket.on("channel.add", async ({result, session_id}: ChannelRowPayload) => {
            if (!result) return;
            if (this.isStaleSession(session_id)) return;
            const {data, channel, row} = result;
            const channelObj = store.channelManager.getChannel(channel);
            if (!channelObj) return;

            this.runChannelOp(channelObj, UpdateOrigin.ChannelAdd, (handle) =>
                this.applyRowWrite(channelObj, channel, row, data, "insert", handle));
        });

        // see docs/EXTRACTED_COMMENTS.md#c712
        this.socket.on("channel.outdated", async ({result}: ChannelOutdatedPayload) => {
            if (!result) return;
            const {channel} = result;
            const channelObj = store.channelManager.getChannel(channel);
            if (!channelObj) return;
            channelObj.setExpectedRows(-1);
            // A bootstrapping channel stays on the inline path (nothing queued
            // for it exists to order behind); a live one enters the queued
            // pending mode.
            if (!channelObj.isBootstrapping()) channelObj.markPending();
            this.bumpChannelStall(channelObj);
        });

        this.socket.on("channel.update", async ({result, session_id}: ChannelRowPayload) => {
            if (!result) return;
            if (this.isStaleSession(session_id)) return;

            const {data, channel, row} = result;
            const channelObj = store.channelManager.getChannel(channel);
            if (!channelObj) return;

            this.runChannelOp(channelObj, UpdateOrigin.ChannelUpdate, (handle) =>
                this.applyRowWrite(channelObj, channel, row, data, "merge", handle));
        });

        this.socket.on("channel_subscribe", async ({result, session_id}: ChannelSubscribePayload) => {
            if (!result) return;
            if (this.isStaleSession(session_id)) return;
            const {channel_id, rows} = result;
            const channelObj = store.channelManager.getChannel(channel_id);
            if (!channelObj) {
                return;
            }
            channelObj.setExpectedRows(rows);
            this.bumpChannelStall(channelObj);
            // see docs/EXTRACTED_COMMENTS.md#c713
            this.maybeFinalizeChannel(channelObj)
                .catch(SocketListener.logChannelOpError);
        })

        this.socket.on("channel.delete", async ({result, session_id}: ChannelDeletePayload) => {
            if (!result) return;
            if (this.isStaleSession(session_id)) return;

            const {channel, row} = result;
            const channelObj = store.channelManager.getChannel(channel);
            if (!channelObj) return;

            const applyDelete = async (handle?: UpdateContextHandle) => {
                if (handle && !handle.active) return;
                const rows = channelObj.getRows().slice();
                const removed = rows.splice(row, 1).length > 0;
                channelObj.setRows(rows, true);
                // Mirror the server-side removal in expected_rows — the counterpart
                // of the channel.add increment in applyRowWrite. Only when a row was
                // actually removed, so client count and expected stay consistent.
                if (removed) {
                    const expected = channelObj.getExpectedRows();
                    if (expected > 0) channelObj.setExpectedRows(expected - 1);
                }
                await postUpdate({channel, row, data: [] as any}, "DATA_REMOVE");

                // see docs/EXTRACTED_COMMENTS.md#c714
                if (channelObj.isPending()) {
                    await this.maybeFinalizeChannel(channelObj, handle);
                    return;
                }

                // see docs/EXTRACTED_COMMENTS.md#c743
                if (!store.loaded) return;

                const affected = this.collectAffectedSubsForRow(channelObj, row, true);

                if (affected.length > 0) {
                    await Promise.all(Array.from(affected
                        .map(affect => affect.sub.updateData("", -1, [], handle))));
                }

                // see docs/EXTRACTED_COMMENTS.md#c467
                // Membership changed because the row array shrank — that is not
                // conditional on a subscription being bound at or above the removed
                // row. Deletes past the last bound row reach here now that the
                // pre-enqueue affected-subs guard is gone; they must still invalidate.
                // see docs/EXTRACTED_COMMENTS.md#c657
                void postRenderRequest(["membershipChange"], []);
            };

            this.runChannelOp(channelObj, UpdateOrigin.ChannelDelete, applyDelete);
        });

        // Whole-channel reinit: the server rebuilt the channel, so local data
        // is void. Freeze the channel, blank it (rows + workers + every sub on
        // it, in one context), resubscribe; the replay + announce finalize it
        // like any other (re)bootstrap.
        this.socket.on("channel.reset", async ({result, session_id}: ChannelDeletePayload) => {
            if (!result) return;
            if (this.isStaleSession(session_id)) return;
            const channelObj = store.channelManager.getChannel(result.channel);
            if (!channelObj) return;
            const name = String(channelObj.getName());

            // Mid-rebuild there is nothing to do: the rebuild tears down and
            // re-requests every channel anyway, and an unsubscribe here would
            // race the subscriptions it is about to create.
            if (!store.socketManager.isLayoutConfirmedForSubscriptions()) return;

            const {channel} = result;
            channelObj.setExpectedRows(-1);
            if (!channelObj.isBootstrapping()) channelObj.markPending();

            // see docs/EXTRACTED_COMMENTS.md#c804
            const droppedOps = orchestrator.dropChannelOps(name, SocketListener.RESET_DROPPED_ORIGINS);
            if (droppedOps > 0) {
                console.log(`[SocketListener] channel.reset ${name}: dropped ${droppedOps} queued pre-reset row op(s)`);
            }

            // see docs/EXTRACTED_COMMENTS.md#c718
            this.runChannelOp(channelObj, UpdateOrigin.ChannelReset, async (handle) => {
                if (handle && !handle.active) return;
                channelObj.setRows([], true);
                await postUpdate({channel, row: -1, data: [] as any}, "DATA_REMOVE");

                // see docs/EXTRACTED_COMMENTS.md#c803
                if (!handle) return;
                const subs = [...channelObj.getSubscriptions().values()] as WrapperSubscription[];
                await Promise.all(subs.map((sub) =>
                    sub.updateData("", -1, [], handle).catch((error) => {
                        console.warn(
                            `[SocketListener] reset clear failed for ${sub.componentId}:`, error);
                    })
                ));
                void postRenderRequest(["membershipChange"], []);
            });

            // see docs/EXTRACTED_COMMENTS.md#c719
            store.socketManager.removeChannel(name);
            store.socketManager.requestChannel(name, getChannelSubscriptionFields(name));
        });

        this.socket.on("channel.clear", async ({result, session_id}: ChannelDeletePayload) => {
            if (!result) return;
            if (this.isStaleSession(session_id)) return;

            const {channel, row} = result;
            const channelObj = store.channelManager.getChannel(channel);
            if (!channelObj) return;

            const applyClear = async (handle?: UpdateContextHandle) => {
                if (handle && !handle.active) return;
                const currentRows = channelObj.getRows();
                currentRows[row] = null;
                channelObj.setRows(currentRows, true);
                await postUpdate({channel, row, data: null}, "DATA_UPDATE");

                // Frozen while pending — the finalize pass repaints every sub at once.
                if (channelObj.isPending()) return;

                // see docs/EXTRACTED_COMMENTS.md#c743
                if (!store.loaded) return;

                // see docs/EXTRACTED_COMMENTS.md#c468
                const channelName = channelObj.getName();
                for (const [key, subscription] of channelObj.getSubscriptions()) {
                    const item = store.components[key];
                    if (!item?.dynamic) continue;

                    const primaryRowMatch = item.dynamic.row != null && item.dynamic.row == row;
                    const secondaryRowMatch = item.function_props
                        && item.function_props.second_channel != null
                        && `${item.function_props.second_channel}` === channelName
                        && (item.function_props.second_row ?? 0) === row;

                    if (primaryRowMatch || secondaryRowMatch) {
                        await subscription.handleSubscription(null, row, channel, false, 1, handle);
                    }
                }

                // see docs/EXTRACTED_COMMENTS.md#c469
                void postRenderRequest(["membershipChange"], []);
            };

            this.runChannelOp(channelObj, UpdateOrigin.ChannelClear, applyClear);
        });

        /* ------------------------ Generic message log ---------------------- */

        this.socket.on("message", ({message, error, result}: GenericMessagePayload) => {
            switch (message) {
                case "Could not subscribe to channel": {
                    console.warn("[Socket] Subscription failed:", result, error);
                    const failed = store.channelManager.getChannel(String(result));
                    if (!failed) return;
                    // No announce will ever come for this channel — stop
                    // waiting on it instead of freezing its subs (and the
                    // boot gate) until the stall net fires.
                    this.acceptChannelAsIs(failed);
                    break;
                }
                case "Successfully subscribed to channel":
                    // No-op for now
                    break;
                default:
                    console.log("[Socket]", message);
            }
        });
    }

    /* see docs/EXTRACTED_COMMENTS.md#c726 */
    private static isPlaylistNotFound(payload: RequestPlaylistPayload): boolean {
        const text = `${payload.message ?? ""} ${payload.error ?? ""}`.toLowerCase();
        return text.includes("playlist") && text.includes("not found");
    }

    /* see docs/EXTRACTED_COMMENTS.md#c727 */
    private handlePlaylistNotFound(payload: RequestPlaylistPayload): void {
        const store = useStore();
        const result = payload.result;
        const name = typeof result === "string" ? result : result?.playlist_name;
        if (typeof name !== "string" || name === "") {
            console.warn("[Socket] Playlist not found, but the reply named no playlist:", payload);
            return;
        }
        console.warn(`[Socket] Playlist ${name} was not found — hiding its objects.`);
        store.playlistManager.markMissing(name);
    }

    /* see docs/EXTRACTED_COMMENTS.md#c782 */
    private static isRuleNotFound(payload: RequestRulePayload): boolean {
        const text = `${payload.message ?? ""} ${payload.error ?? ""}`.toLowerCase();
        return text.includes("rule") && text.includes("not found");
    }

    private handleRuleNotFound(payload: RequestRulePayload): void {
        const result = payload.result;
        const name = typeof result === "string" ? result : result?.name;
        if (typeof name !== "string" || name === "") {
            console.warn("[Socket] Rule not found, but the reply named no rule:", payload);
            return;
        }
        console.warn(`[Socket] Rule ${name} was not found.`);
        useStore().ruleManager.markMissing(name);
    }

    /* see docs/EXTRACTED_COMMENTS.md#c470 */
    public resetInitialLoadState(rebuildPending: boolean = true) {
        const store = useStore();
        store.loaded = false;
        this.initialFired = false;
        if (rebuildPending) this.subscriptionsActivated = false;
        // A rebuilt tree has fresh subscriptions, so they earn the retry again.
        this.unmatchedRetryDone = false;
    }

    /* see docs/EXTRACTED_COMMENTS.md#c471 */
    public markSubscriptionsActivated() {
        this.subscriptionsActivated = true;
        // The subscribe requests just went out — every frozen channel's silence
        // window starts now, not at its construction.
        for (const ch of useStore().channelManager.getChannels()) {
            if (ch.isPending()) ch.touch();
        }
        this.bumpChannelStall();
        // see docs/EXTRACTED_COMMENTS.md#c472
        this.maybeFireInitial();
    }

    public isSubscriptionsActivated(): boolean {
        return this.subscriptionsActivated;
    }

    /* see docs/EXTRACTED_COMMENTS.md#c473 */
    private channelsReady(): boolean {
        const store = useStore();
        for (const channel of store.channelManager.getChannels()) {
            if (channel.isPending()) return false;
        }
        return true;
    }

    /* see docs/EXTRACTED_COMMENTS.md#c475 */
    private maybeFireInitial(): void {
        if (this.initialFired) return;
        // see docs/EXTRACTED_COMMENTS.md#c476
        if (!this.subscriptionsActivated) return;
        if (!this.channelsReady()) return;
        this.initialFired = true;
        void this.fireInitial().catch((err) => {
            console.error("[SocketListener] fireInitial rejected", err);
            Sentry.captureException(err, {
                tags: {component: "SocketListener", path: "fireInitial"},
            });
        });
    }

    /* see docs/EXTRACTED_COMMENTS.md#c480 */
    private async reconcileChannelRowCounts(): Promise<void> {
        const store = useStore();

        // see docs/EXTRACTED_COMMENTS.md#c481
        const overLong: Array<{ channelObj: any; channelId: number; expected: number; excess: number }> = [];
        for (const channel of store.channelManager.getChannels()) {
            // A pending channel trims itself at finalize — its count is not settled yet.
            if (channel.isPending()) continue;
            const expected = channel.getExpectedRows();
            if (expected < 0) continue;
            const excess = channel.getRows().length - expected;
            if (excess <= 0) continue;
            overLong.push({ channelObj: channel, channelId: parseInt(channel.getName()), expected, excess });
        }
        if (overLong.length === 0) return;

        // see docs/EXTRACTED_COMMENTS.md#c483
        await orchestrator.enqueue(UpdateOrigin.ChannelDelete, async (handle) => {
            if (!handle.active) return;
            for (const { channelObj, channelId, expected, excess } of overLong) {
                console.warn(
                    `[SocketListener] Reconnect reconcile: channel ${channelObj.getName()} ` +
                    `holds ${channelObj.getRows().length} rows, server expects ${expected} — ` +
                    `deleting ${excess} ghost tail row(s)`
                );
                await this.trimChannelToExpected(channelObj, channelId, expected, handle);
            }

            // see docs/EXTRACTED_COMMENTS.md#c487
            void postRenderRequest(["membershipChange"], []);
        });
    }

    /** Trim one channel's local rows down to the server-announced count and
     *  flush every subscription bound to it. Shared by the reconnect/initial
     *  reconcile sweep and the channel_subscribe announce trim. The caller
     *  owns the render invalidation. */
    private async trimChannelToExpected(
        channelObj: any,
        channelId: number,
        expected: number,
        handle?: UpdateContextHandle,
        refreshSubs: boolean = true,
    ): Promise<void> {
        // see docs/EXTRACTED_COMMENTS.md#c484
        const rows = channelObj.getRows();
        const excess = rows.length - expected;
        if (excess <= 0) return;
        rows.splice(expected);
        channelObj.setRows(rows, true);

        // see docs/EXTRACTED_COMMENTS.md#c485
        if (expected === 0) {
            await postUpdate({ channel: channelId, row: -1, data: [] as any }, "DATA_REMOVE");
        } else {
            for (let i = 0; i < excess; i++) {
                await postUpdate({ channel: channelId, row: expected, data: [] as any }, "DATA_REMOVE");
            }
        }

        // see docs/EXTRACTED_COMMENTS.md#c486
        if (!refreshSubs) return;
        const affected = this.collectAffectedSubsForRow(channelObj, 0, true);
        await Promise.all(affected.map((a) => a.sub.updateData("", -1, [], handle)));
    }


    private static coverGrowth(channelObj: any, rows: number, inserted: boolean): void {
        const expected = channelObj.getExpectedRows();
        if (expected < 0) return;
        const next = Math.max(inserted ? expected : expected, rows);
        if (next !== expected) channelObj.setExpectedRows(next);
    }


    private static logChannelOpError(err: unknown): void {
        if (err instanceof Error && err.message.includes("dropped")) return;
        console.warn("[SocketListener] channel op failed:", err);
    }

    /** Single dispatch point for every channel row op.
     *
     *  Bootstrapping channel → run the body inline, no orchestrator context
     *  (see docs/EXTRACTED_COMMENTS.md#c720). Otherwise enqueue directly —
     *  order is frozen at enqueue time; a deferred enqueue (the old
     *  requestIdleCallback hop) let a later op overtake an insert and merge
     *  into pre-shift rows. */
    private runChannelOp(
        channelObj: Channel,
        origin: UpdateOrigin,
        body: (handle?: UpdateContextHandle) => Promise<void>,
    ): void {
        this.bumpChannelStall(channelObj);
        if (channelObj.isBootstrapping()) {
            body().catch(SocketListener.logChannelOpError);
            return;
        }
        orchestrator.enqueue(origin, (handle) => body(handle), String(channelObj.getName()))
            .catch(SocketListener.logChannelOpError);
    }

    /* see docs/EXTRACTED_COMMENTS.md#c721 */
    private bumpChannelStall(activeChannel?: Channel): void {
        activeChannel?.touch();
        if (this.channelStallTimer) clearTimeout(this.channelStallTimer);
        this.channelStallTimer = undefined;
        let oldest = Infinity;
        for (const ch of useStore().channelManager.getChannels()) {
            if (ch.isPending()) oldest = Math.min(oldest, ch.getLastActivity());
        }
        if (oldest === Infinity) return;
        const delay = Math.max(0, oldest + SocketListener.CHANNEL_STALL_IDLE_MS - Date.now());
        this.channelStallTimer = window.setTimeout(() => {
            this.channelStallTimer = undefined;
            // A superseded listener (fresh socket per attempt) must not fire
            // into the new session's replay — its timer never sees the bumps.
            if (useStore().socketManager.socketListener !== this) return;
            const now = Date.now();
            for (const ch of useStore().channelManager.getChannels()) {
                if (!ch.isPending()) continue;
                const silentMs = now - ch.getLastActivity();
                if (silentMs < SocketListener.CHANNEL_STALL_IDLE_MS) continue;
                console.warn(
                    `[SocketListener] Channel ${ch.getName()} silent for ${silentMs}ms — going live with ` +
                    `${ch.getRows().length} row(s), announced ${ch.getExpectedRows()}.`
                );
                this.acceptChannelAsIs(ch);
            }
            this.bumpChannelStall();
        }, delay);
    }

    /** Stop waiting on a channel: whatever it holds is the truth. Late rows or
     *  a late announce self-heal through the normal live path. */
    private acceptChannelAsIs(channelObj: Channel): void {
        // Restart its window: a channel the accept cannot release yet (its
        // finalize hop still queued) is re-checked in 10s, not in a tight loop.
        channelObj.touch();
        channelObj.setExpectedRows(channelObj.getRows().length);
        this.maybeFinalizeChannel(channelObj).catch(SocketListener.logChannelOpError);
    }

    /* see docs/EXTRACTED_COMMENTS.md#c488 */
    private isStaleSession(sessionId?: string): boolean {
        if (sessionId == null) return false;
        return Number(sessionId) !== this.socketManager.getUpdateQueueManager().getSessionId();
    }

    /* see docs/EXTRACTED_COMMENTS.md#c489 */
    private async applyRowWrite(
        channelObj: any,
        channel: number,
        row: number,
        data: any,
        mode: "insert" | "merge",
        handle?: UpdateContextHandle,
    ): Promise<void> {
        if (handle && !handle.active) return;
        const store = useStore();
        const currentRows = channelObj.getRows();
        const extended = row >= currentRows.length;

        // see docs/EXTRACTED_COMMENTS.md#c490
        const insertShift = mode === "insert" && !extended;

        let noopMerge = false;
        if (extended) {
            // see docs/EXTRACTED_COMMENTS.md#c491
            if (currentRows.length < row) currentRows.length = row;
            currentRows[row] = data;
        } else if (insertShift) {
            // see docs/EXTRACTED_COMMENTS.md#c492
            currentRows.splice(row, 0, data);
        } else {
            // In-place merge (channel.update on an existing row).
            // mergeRow returns the same instance when no field changed.
            const prev = currentRows[row];
            const merged = data == null ? {} : mergeRow(prev, data);
            noopMerge = merged === prev;
            if (!noopMerge) currentRows[row] = merged;
        }
        if (!noopMerge) channelObj.setRows(currentRows, true);

        // see docs/EXTRACTED_COMMENTS.md#c493
        const grew = extended || insertShift;

        if (grew) {
            SocketListener.coverGrowth(channelObj, currentRows.length, mode === "insert");
        }

        await postUpdate({channel, row, data}, grew ? "DATA_INSERT" : "DATA_UPDATE");

        // see docs/EXTRACTED_COMMENTS.md#c714
        if (channelObj.isPending()) {
            await this.maybeFinalizeChannel(channelObj, handle);
            return;
        }

        // A merge that changed no field fans out nothing — see #c723.
        if (noopMerge) return;

        // An inline (bootstrapping) write never fans out, even if the channel
        // finalized while this write awaited its worker ack — the finalize
        // refresh covers it.
        if (!handle) return;

        // see docs/EXTRACTED_COMMENTS.md#c743
        if (!store.loaded) return;

        // see docs/EXTRACTED_COMMENTS.md#c494
        if (handle.sessionId != null &&
            !store.socketManager.getUpdateQueueManager().isActiveSession(handle.sessionId)) {
            return;
        }
        const affected = this.collectAffectedSubsForRow(channelObj, row, insertShift);
        await Promise.all(affected.map(a => a.sub.updateData("", -1, [], handle)));

        // see docs/EXTRACTED_COMMENTS.md#c495
        if (insertShift) {
            void postRenderRequest(["membershipChange"], []);
        }

        // see docs/EXTRACTED_COMMENTS.md#c496
        if (!grew) return;

        // see docs/EXTRACTED_COMMENTS.md#c497
        const growthRow = insertShift ? currentRows.length - 1 : row;
        this.maybeExtendDatasheets(growthRow);
    }

    /* see docs/EXTRACTED_COMMENTS.md#c498 */
    private maybeExtendDatasheets(growthRow: number): void {
        const store = useStore();
        const extendIds: string[] = [];
        for (const sheetId of (store.datasheets ?? [])) {
            const sheet = store.components[sheetId];
            if (!sheet) continue;

            // see docs/EXTRACTED_COMMENTS.md#c499
            const capacity = datasheetRowCapacity(sheet, store.components);
            if (capacity <= 0) continue;

            // see docs/EXTRACTED_COMMENTS.md#c500
            /* see docs/EXTRACTED_COMMENTS.md#c750 */
            /* see docs/EXTRACTED_COMMENTS.md#c764 */
            const slot = growthRow
                - (this.layoutManager?.startRowFor(sheetId) ?? 0)
                - (this.layoutManager?.rowBaseFor(sheetId) ?? 0);

            // see docs/EXTRACTED_COMMENTS.md#c501
            if (slot >= (sheet.children?.length ?? 0) && slot < capacity) {
                extendIds.push(sheetId);
            }
        }
        if (extendIds.length > 0) {
            this.layoutManager?.resetCalculatedRowFlags(extendIds);
            for (const sheetId of extendIds) {
                triggerDatasheetBuild(sheetId, "extend");
            }
        }
    }

    /* see docs/EXTRACTED_COMMENTS.md#c717 */
    private async maybeFinalizeChannel(channelObj: Channel, handle?: UpdateContextHandle): Promise<void> {
        if (!channelObj.isPending()) return;
        const expected = channelObj.getExpectedRows();
        if (expected < 0) return;
        const rows = channelObj.getRows();
        if (rows.length < expected) return;

        // Mid-session finalize needs a context for the full refresh; hop into
        // the queue when called without one (announce handler, inline writes).
        // Preconditions re-check on execution. The "finalizing" state caps it
        // at one queued ChannelSync hop per channel.
        if (!handle && this.initialFired) {
            if (channelObj.isFinalizeQueued()) return;
            channelObj.markFinalizing();
            orchestrator.enqueue(UpdateOrigin.ChannelSync, async (h) => {
                // Hop is executing — revert so a failed re-check can re-queue.
                if (channelObj.isFinalizeQueued()) channelObj.markPending();
                await this.maybeFinalizeChannel(channelObj, h);
            }).catch((error) => {
                if (channelObj.isFinalizeQueued()) channelObj.markPending();
                SocketListener.logChannelOpError(error);
            });
            return;
        }
        channelObj.markLive();
        // Re-evaluate the stall net: disarms when this was the last frozen
        // channel, re-arms a fresh window for the ones still waiting.
        this.bumpChannelStall();

        if (rows.length > expected) {
            await this.trimChannelToExpected(
                channelObj, parseInt(channelObj.getName()), expected, handle, false);
        }

        // During (re)bootstrap the blanket fireInitial pass refreshes every
        // subscription anyway; the per-channel refresh is for channels that go
        // pending mid-session (channel.outdated / channel.reset).
        if (!this.initialFired) {
            this.maybeFireInitial();
            return;
        }

        // One full pass over every subscription bound to the channel —
        // datasheet rows included; nothing is filtered out here.
        const subs = [...channelObj.getSubscriptions().values()] as WrapperSubscription[];
        await Promise.all(subs.map((sub) =>
            sub.updateData("", -1, [], handle).catch((error) => {
                console.warn(
                    `[SocketListener] finalize refresh failed for ${sub.componentId}:`, error);
            })
        ));
        void postRenderRequest(["membershipChange"], []);
        if (channelObj.getRows().length > 0) {
            this.maybeExtendDatasheets(channelObj.getRows().length - 1);
        }
        this.maybeFireInitial();
    }

    /**
     * One retry, at the end of bootstrap, for the dynamic lookups that found no match.
     *
     * A missed lookup is unreachable afterwards: collectAffectedSubsForRow() finds a subscription
     * through its `dynamic.row`, its function_props second row, or its `lastMatchedRow`, and a miss
     * has none of those — so no later row write in the channel it searched can wake it, and it stays
     * unmatched for the life of the layout. The global re-resolve in fireInitial() does not cover
     * these either: it can run before the rows have reached the DataWorker, and datasheet rows are
     * built after it. (Verifier finding on
     * layout 1010: destination names rendered blank because the first bulk resolve ran while the
     * worker held 0 of channel 10's 560 rows.)
     *
     * Deliberately ONE pass, and only here. updateData("", -1, []) is a *forced* refresh, which by
     * design ignores the request-sequence guard in WrapperSubscription (see the `debugForceRefresh`
     * branch), so a response computed against older data can land after a newer one and leave a
     * stale hide_flags.data behind. Retrying on every row-write burst instead cost layout 1020 seven
     * wrongly-hidden objects. By the end of fireInitial() every channel is complete and acked by the
     * workers, the datasheet builds ahead of this in the FIFO update queue have run, and nothing
     * else is in flight — so a retry here has complete data to match against and nothing to clobber.
     */
    private async retryUnmatchedDynamicSubs(): Promise<void> {
        if (this.unmatchedRetryDone) return;
        this.unmatchedRetryDone = true;

        const store = useStore();
        // see docs/EXTRACTED_COMMENTS.md#c655
        await orchestrator.enqueue(UpdateOrigin.ChannelUpdate, async (handle) => {
            if (!handle.active) return;
            const targets: Array<{ id: string; sub: WrapperSubscription }> = [];
            for (const item of Object.values(store.components)) {
                const dyn = item.dynamic;
                // Only a lookup that scans for a match can be stuck; a resolved one stays reachable
                // through its lastMatchedRow.
                if (!dyn?.is_dynamic || dyn.found === true) continue;
                if (!item.subscription) continue;
                targets.push({id: item.props.id, sub: item.subscription as WrapperSubscription});
            }
            if (targets.length === 0) return;

            console.log(`[SocketListener] bootstrap complete — retrying ${targets.length} unmatched dynamic lookup(s)`);
            await Promise.all(targets.map(t => t.sub.updateData("", -1, [], handle)));
        }).catch((err) => {
            console.error("[SocketListener] unmatched-lookup retry rejected", err);
        });
    }

    /* see docs/EXTRACTED_COMMENTS.md#c780 */
    private collectAffectedSubsForRow(channelObj: any, row: number, shifted: boolean): Array<{ id: string; sub: WrapperSubscription }> {
        const store = useStore();
        const affected: Array<{ id: string; sub: WrapperSubscription }> = [];
        const seen = new Set<string>();
        const channelName = channelObj.getName();

        // Read-only scan over every subscription of the channel: read raw objects,
        // since each read through the store's reactive proxies costs ~15x more.
        // The pushed subscription stays the reactive one the proxied iteration
        // yields, so callers get the same reference as before.
        const components = toRaw(store.components);
        const reactiveChannel = isReactive(channelObj);
        const wrap = (s: any) => reactiveChannel ? reactive(s) : s;
        for (const [key, subscription] of toRaw(channelObj).getSubscriptions()) {
            const item = components[key];
            if (!item?.dynamic) continue;

            // see docs/EXTRACTED_COMMENTS.md#c654
            const primaryRow = item.dynamic.row
                ?? ((item.dynamic.is_dynamic && (item.dynamic.dynamic_fields?.length ?? 0) > 0) ? 0 : null);

            const secondaryChannel = item.function_props?.second_channel;
            const secondaryRow = item.function_props ? (item.function_props.second_row ?? 0) : null;

            let secondaryAffected = false;
            let lastMatchedAffected = false;
            let dynamicAffected = false;
            let primaryRowAffected = false;

            secondaryAffected = item.function_props != null
                && secondaryChannel != null
                && `${secondaryChannel}` === channelName
                && secondaryRow != null
                && (shifted ? secondaryRow >= row : secondaryRow == row);

            dynamicAffected = subscription.lastMatchedRow == null && (
                item.dynamic.dynamic_channel == channelName ||
                item.dynamic.dynamic_key_channel == channelName
            );

            lastMatchedAffected = subscription.lastMatchedRow != null
                && subscription.lastMatchedCh != null
                && `${subscription.lastMatchedCh}` === channelName
                && subscription.lastMatchedRow >= row;

            primaryRowAffected = primaryRow != null && (shifted ? primaryRow >= row : primaryRow == row);

            if (primaryRowAffected || secondaryAffected || lastMatchedAffected || dynamicAffected) {
                if (!seen.has(key)) {
                    seen.add(key);
                    affected.push({id: key, sub: wrap(subscription) as WrapperSubscription});
                }
            }
        }
        return affected;
    }

    /* see docs/EXTRACTED_COMMENTS.md#c502 */

    private async fireInitial() {
        const store = useStore();
        console.log(`[fireInitial] entry: datasheets=${(store.datasheets ?? []).length}, components=${Object.keys(store.components).length}`);

        // see docs/EXTRACTED_COMMENTS.md#c503
        await orchestrator.enqueue(UpdateOrigin.Initial, async (handle) => {
            if (!handle.active) {
                console.warn("[fireInitial] body aborted at entry: handle.active=false");
                this.initialFired = false;
                return;
            }
            // Datasheet items are deliberately included: their subscriptions are
            // as stale as everyone else's after a (re)load, and the datasheet
            // build below only covers rows it (re)creates.
            const eligibleIds = Object.keys(store.components)
                .filter((id) => !!store.components[id].subscription);
            const eligible = eligibleIds.map((id) => store.components[id]);

            const slowPathAt = slowPathMark();
            await Promise.all(
                eligible.map((item) => item.subscription.updateData("", -1, [], handle))
            );
            if (!handle.active) {
                console.warn("[fireInitial] body aborted after updateData: handle.active=false — store.loaded NOT set");
                this.initialFired = false;
                return;
            }
            // This pass already refreshed the datasheet rows buffered before load;
            // without this the buffer replays them as a second Initial pass.
            dropRefreshedFromSlowPathBuffer(eligibleIds, slowPathAt);
            store.loaded = true;
            console.log("[fireInitial] body done: store.loaded=true");
            // see docs/EXTRACTED_COMMENTS.md#c504
            CacheManager.instance.markDirty();
            void CacheManager.instance.commitSnapshot("store-loaded");
        }).catch((err) => {
            console.error("[fireInitial] Initial iteration rejected", err);
            // see docs/EXTRACTED_COMMENTS.md#c745
            this.initialFired = false;
            Sentry.captureException(err, {
                tags: {component: "SocketListener", path: "fireInitial.initial"},
            });
        });

        try {
            // see docs/EXTRACTED_COMMENTS.md#c505
            for (const id of Object.keys(store.components)) {
                postUpdateGeometry(id, "initial-bootstrap-kick");
            }
            await postRenderRequest(["initialBootstrapComplete"]);

            // see docs/EXTRACTED_COMMENTS.md#c506
            /* see docs/EXTRACTED_COMMENTS.md#c751 */
            const sheetIds = [...(store.datasheets ?? [])].sort();
            this.layoutManager?.resetCalculatedRowFlags(sheetIds);
            for (const datasheetId of sheetIds) {
                if (!store.components[datasheetId]) continue;
                triggerDatasheetBuild(datasheetId, "initial");
            }
        } catch (err) {
            console.error("[SocketListener] fireInitial post-queueUpdate block threw — sweep/kick skipped", err);
            Sentry.captureException(err, {
                tags: {component: "SocketListener", path: "initializeData.postQueue"},
            });
        }

        // see docs/EXTRACTED_COMMENTS.md#c507
        await this.reconcileChannelRowCounts();

        // Last: give the lookups that missed while the channels were still filling one retry against
        // the complete data. See retryUnmatchedDynamicSubs().
        await this.retryUnmatchedDynamicSubs();
    }

}